import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { CloudUpload, ArrowBack } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useFormik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { DefaultPage } from "../../../layouts/default-page";
import { Notify } from "../../../layouts/notify";
import { BoardMember } from "../../../models/BoardMember";
import { upload } from "../../../services/Upload";
import BoardMemberValidationsForm from "./validation";
import {
  useAddBoardMemberMutation,
  useUpdateBoardMemberMutation,
} from "../../../services/appointees.query";

export default function BoardMemberDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const boardMemberData = location.state?.boardMember;

  const [addBoardMember, { isLoading: addBoardMemberLoading }] =
    useAddBoardMemberMutation();
  const [updateBoardMember, { isLoading: updateBoardMemberLoading }] =
    useUpdateBoardMemberMutation();

  const [profileUrl, setProfileUrl] = useState<File | string | null>(
    boardMemberData?.profile_url ?? null
  );

  const [appointmentDate, setAppointmentDate] = useState<Dayjs | null>(
    boardMemberData?.appointment_date
      ? dayjs(boardMemberData.appointment_date)
      : dayjs()
  );

  const formik = useFormik({
    initialValues: {
      first_name: boardMemberData ? boardMemberData.first_name : "",
      middle_initial: boardMemberData
        ? boardMemberData.middle_initial?.replace(/\./g, "")
        : "",
      last_name: boardMemberData ? boardMemberData.last_name : "",
      appointment_date: boardMemberData
        ? boardMemberData.appointment_date
        : dayjs().format("YYYY-MM-DD"),
      description: boardMemberData ? boardMemberData.description : "",
      profile_file: boardMemberData ? boardMemberData.profile_url : null,
      is_active: boardMemberData ? Boolean(boardMemberData.is_active) : true,
      role: boardMemberData ? boardMemberData.role : "BOARD",
    },
    validationSchema: BoardMemberValidationsForm,
    onSubmit: async (values) => {
      // Clean middle initial so no double periods occur
      const rawMI = values.middle_initial?.replace(/\./g, "").trim() || "";
      const formattedMI = rawMI ? `${rawMI}.` : "";

      let formData: BoardMember = {
        first_name: values.first_name.trim(),
        middle_initial: formattedMI,
        last_name: values.last_name.trim(),
        appointment_date: values.appointment_date,
        description: values.description?.trim() || "",
        profile_url: "",
        is_active: values.is_active,
        role: "BOARD",
      };

      if (boardMemberData) {
        formData.id = boardMemberData.id;
      }

      if (profileUrl && typeof profileUrl !== "string") {
        const file = profileUrl as File;
        const fileExt = file.name.split(".").pop();
        const filePath = `${uuidv4()}-${Math.random()}.${fileExt}`;
        try {
          let data = await upload(file, "images/board_members", filePath);
          formData.profile_url = data.url;
        } catch (uploadError) {
          console.error("Image upload failed:", uploadError);
          Notify("error", "Failed to upload profile image.");
          return;
        }
      } else if (typeof profileUrl === "string") {
        formData.profile_url = profileUrl;
      } else if (boardMemberData?.profile_url) {
        formData.profile_url = boardMemberData.profile_url;
      }

      try {
        if (!boardMemberData) {
          await addBoardMember(formData).unwrap();
          Notify("success", "Board Member created successfully");
        } else {
          await updateBoardMember(formData).unwrap();
          Notify("info", "Board Member updated successfully");
        }
        navigate("/app-contents/board-members");
      } catch (error) {
        console.error("Error saving Board Member:", error);
        Notify("error", "Error occurred while saving Board Member");
      }
    },
  });

  useEffect(() => {
    if (boardMemberData) {
      const dateVal = boardMemberData.appointment_date
        ? dayjs(boardMemberData.appointment_date)
        : null;
      setAppointmentDate(dateVal);
      formik.setFieldValue("appointment_date", dateVal ? dateVal.format("YYYY-MM-DD") : "");
      formik.setFieldValue("profile_file", boardMemberData.profile_url);
    }
  }, [boardMemberData]);

  const handleAvatarImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileUrl(file);
      formik.setFieldValue("profile_file", file);
      formik.setFieldError("profile_file", undefined);
    }
  };

  const handleAppointmentDateChange = (newValue: Dayjs | null) => {
    setAppointmentDate(newValue);
    formik.setFieldValue(
      "appointment_date",
      newValue ? newValue.format("YYYY-MM-DD") : ""
    );
  };

  const previewAvatarUrl =
    profileUrl && typeof profileUrl !== "string"
      ? URL.createObjectURL(profileUrl)
      : typeof profileUrl === "string"
        ? profileUrl
        : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DefaultPage
        title={boardMemberData ? "Edit Board Member" : "Add Board Member"}
        backButtonLink="/app-contents/board-members"
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 4 },

          }}
        >
          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <Grid container spacing={4}>
              {/* Left Column: Avatar & Image Upload */}
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    borderColor: "#E5E7EB",
                    backgroundColor: "#F9FAFB",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color="text.secondary"
                    mb={2}
                    textTransform="uppercase"
                    letterSpacing={0.5}
                  >
                    Profile Image
                  </Typography>

                  <Avatar
                    src={previewAvatarUrl}
                    alt="Board Member Avatar"
                    sx={{
                      width: 140,
                      height: 140,
                      mb: 2.5,
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                      border: "3px solid #FFFFFF",
                      bgcolor: "#E0E7FF",
                      color: "#3730A3",
                      fontSize: 48,
                    }}
                  >
                    {formik.values.first_name?.[0]?.toUpperCase() || "B"}
                  </Avatar>

                  <input
                    accept="image/*"
                    style={{ display: "none" }}
                    id="avatar_file_input"
                    type="file"
                    onChange={handleAvatarImageChange}
                  />

                  <label htmlFor="avatar_file_input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUpload />}
                      size="small"
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                    >
                      {previewAvatarUrl ? "Change Image" : "Upload Image"}
                    </Button>
                  </label>

                  {profileUrl && typeof profileUrl !== "string" && (
                    <Typography fontSize={12} color="text.secondary" mt={1}>
                      {profileUrl.name}
                    </Typography>
                  )}

                  {formik.errors.profile_file && !boardMemberData && (
                    <Typography fontSize={12} color="error" mt={1}>
                      {String(formik.errors.profile_file)}
                    </Typography>
                  )}
                </Card>
              </Grid>

              {/* Right Column: Member Details */}
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  <DatePicker
                    label="Appointment Date"
                    value={appointmentDate}
                    onChange={handleAppointmentDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "medium",
                        error:
                          formik.touched.appointment_date &&
                          Boolean(formik.errors.appointment_date),
                        helperText:
                          formik.touched.appointment_date &&
                          (formik.errors.appointment_date as string),
                      },
                    }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="First Name"
                        name="first_name"
                        placeholder="e.g. Frances"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.first_name &&
                          Boolean(formik.errors.first_name)
                        }
                        helperText={
                          formik.touched.first_name &&
                          (formik.errors.first_name as string)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        label="M.I."
                        name="middle_initial"
                        placeholder="G"
                        inputProps={{ maxLength: 2 }}
                        value={formik.values.middle_initial}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.middle_initial &&
                          Boolean(formik.errors.middle_initial)
                        }
                        helperText={
                          formik.touched.middle_initial &&
                          (formik.errors.middle_initial as string)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="last_name"
                        placeholder="e.g. Abella"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.last_name &&
                          Boolean(formik.errors.last_name)
                        }
                        helperText={
                          formik.touched.last_name &&
                          (formik.errors.last_name as string)
                        }
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description / Biography"
                    name="description"
                    placeholder="Enter board member background, qualifications, and role description..."
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      borderColor: "#E5E7EB",
                    }}
                  >
                    <Box>
                      <Typography fontWeight={600} fontSize={14}>
                        Active Member Status
                      </Typography>
                      <Typography fontSize={12} color="text.secondary">
                        Active members are displayed publicly on the app roster.
                      </Typography>
                    </Box>

                    <Switch
                      checked={formik.values.is_active}
                      onChange={(e) =>
                        formik.setFieldValue("is_active", e.target.checked)
                      }
                      color="primary"
                    />
                  </Paper>
                </Stack>
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => navigate("/app-contents/board-members")}
                disabled={formik.isSubmitting}
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>

              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={
                  formik.isSubmitting ||
                  addBoardMemberLoading ||
                  updateBoardMemberLoading
                }
                sx={{ borderRadius: 2, px: 4, fontWeight: 600 }}
              >
                {boardMemberData ? "Update Board Member" : "Save Board Member"}
              </LoadingButton>
            </Box>
          </form>
        </Paper>
      </DefaultPage>
    </LocalizationProvider>
  );
}
